import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, RotateCcw, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface ConfigItem {
  key: string;
  label: string;
  description: string;
  type: 'number';
  min?: number;
  max?: number;
  step?: number;
}

const STUDENT_CONFIG_ITEMS: ConfigItem[] = [
  {
    key: 'student_error_correction_reward',
    label: '纠错奖励',
    description: '学员提交纠错并通过审核后获得的U币数量',
    type: 'number',
    min: 0,
    max: 500,
    step: 5,
  },
  {
    key: 'student_daily_earning_limit',
    label: '每日获取上限',
    description: '学员每天最多可获得的U币总数（防止刷币）',
    type: 'number',
    min: 10,
    max: 500,
    step: 5,
  },
];

const TEACHER_CONFIG_ITEMS: ConfigItem[] = [
  {
    key: 'teacher_error_correction_reward',
    label: '纠错奖励',
    description: '教师提交纠错并通过审核后获得的U币数量',
    type: 'number',
    min: 0,
    max: 500,
    step: 5,
  },
  {
    key: 'teacher_purchase_rebate_rate',
    label: '购课返利比例(%)',
    description: '学员购课时绑定教师，教师获得的返利比例（基于实际支付金额）',
    type: 'number',
    min: 0,
    max: 50,
    step: 1,
  },
  {
    key: 'teacher_daily_earning_limit',
    label: '每日获取上限',
    description: '教师每天最多可获得的U币总数（防止刷币）',
    type: 'number',
    min: 10,
    max: 500,
    step: 5,
  },
];

export default function PointManagement() {
  const [activeTab, setActiveTab] = useState<'student' | 'teacher'>('student');
  const [configs, setConfigs] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const allKeys = [...STUDENT_CONFIG_ITEMS, ...TEACHER_CONFIG_ITEMS].map(item => item.key);
      const { data, error } = await supabase
        .from('point_settings')
        .select('setting_key, setting_value')
        .in('setting_key', allKeys);

      if (error) throw error;

      const configMap: Record<string, any> = {};
      data?.forEach((item: any) => {
        configMap[item.setting_key] = item.setting_value;
      });

      setConfigs(configMap);
    } catch (error: any) {
      setMessage({ type: 'error', text: '加载配置失败: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleNumberChange = (key: string, value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;

    setConfigs(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), value: numValue },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setMessage({ type: 'error', text: '请先登录' });
        return;
      }

      // 批量更新配置
      const currentItems = activeTab === 'student' ? STUDENT_CONFIG_ITEMS : TEACHER_CONFIG_ITEMS;
      
      for (const item of currentItems) {
        const configValue = configs[item.key];

        const updateData: any = {
          updated_at: new Date().toISOString(),
          updated_by: session.user.id,
        };

        // 数字类型：即使值为0也要保存
        updateData.setting_value = {
          ...(configValue || {}),
          value: configValue?.value ?? 0,
        };

        const { error } = await supabase
          .from('point_settings')
          .update(updateData)
          .eq('setting_key', item.key);

        if (error) throw error;
      }

      setMessage({ type: 'success', text: '配置保存成功！新规则将立即生效' });
    } catch (error: any) {
      setMessage({ type: 'error', text: '保存失败: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (confirm('确定要恢复为当前数据库中的配置吗？')) {
      loadConfigs();
      setMessage({ type: 'success', text: '已恢复为当前数据库中的配置' });
    }
  };

  const getConfigValue = (item: ConfigItem) => {
    const config = configs[item.key];
    if (!config) return 0;
    return config.value ?? 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentItems = activeTab === 'student' ? STUDENT_CONFIG_ITEMS : TEACHER_CONFIG_ITEMS;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">U币管理</h1>
        <p className="text-gray-600 mt-1">
          配置学员端和教师端的U币获取规则。所有参数修改后立即生效，无需重启服务。
        </p>
      </div>

      {/* Tab切换 */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('student')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'student'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          学员端配置
        </button>
        <button
          onClick={() => setActiveTab('teacher')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'teacher'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          教师端配置
        </button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {activeTab === 'student' ? '学员端U币规则' : '教师端U币规则'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {currentItems.map(item => (
            <div key={item.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {item.label}
              </label>
              <p className="text-xs text-gray-500 mb-2">{item.description}</p>
              <input
                type="number"
                value={getConfigValue(item)}
                onChange={(e) => handleNumberChange(item.key, e.target.value)}
                min={item.min}
                max={item.max}
                step={item.step}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          ))}
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="flex gap-4 mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-4 h-4" />
          {saving ? '保存中...' : '保存配置'}
        </button>
        <button
          onClick={handleReset}
          className="flex items-center gap-2 px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          重置
        </button>
      </div>
    </div>
  );
}
